import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, LogOut, TrendingUp, TrendingDown,
  ArrowUpRight, ArrowDownRight, Sparkles, Settings, ChevronDown
} from 'lucide-react';
import { DashboardProvider } from '../../contexts/DashboardContext';
import DashboardTabs from './DashboardTabs';
import DashboardTabContent from './DashboardTabContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// KPI Item Component
const KPIItem = ({ label, value, unit, change, inverse = false, loading = false }) => {
  const isPositive = inverse ? change < 0 : change > 0;
  
  if (loading) {
    return (
      <div className="flex items-center gap-3 px-4 py-2 animate-pulse">
        <div className="w-20 h-8 bg-slate-200 rounded"></div>
      </div>
    );
  }
  
  // Format value based on unit type
  const formatValue = () => {
    if (unit === 'Cr') {
      return `₹${value?.toFixed(2) || '0'} Cr`;
    } else if (unit === '%') {
      return `${value?.toFixed(2) || '0'}%`;
    } else if (unit === 'count') {
      return value?.toLocaleString() || '0';
    }
    return value || '0';
  };
  
  return (
    <div className="flex items-center gap-3 px-4 py-2 border-r border-slate-100 last:border-0">
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-bold text-slate-900">{formatValue()}</span>
          {change !== null && change !== undefined && (
            <span className={`flex items-center text-xs font-medium ${isPositive ? 'text-emerald-600' : 'text-red-500'}`}>
              {isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {Math.abs(change).toFixed(1)}%
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [kpiData, setKpiData] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(true);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setProfileMenuOpen(false);
      }
    };
    if (profileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileMenuOpen]);

  // Fetch KPI data from multiple endpoints
  useEffect(() => {
    const fetchKPIData = async () => {
      try {
        setKpiLoading(true);
        
        // Fetch data from portfolio and risk endpoints
        const [portfolioRes, riskRes, depositsRes] = await Promise.all([
          fetch(`${API_URL}/api/dashboard/portfolio-overview`),
          fetch(`${API_URL}/api/dashboard/risk-asset-quality`),
          fetch(`${API_URL}/api/dashboard/deposits-liquidity`)
        ]);
        
        const portfolioData = await portfolioRes.json();
        const riskData = await riskRes.json();
        const depositsData = await depositsRes.json();
        
        // Calculate KPIs from real data
        const totalLoans = portfolioData.summary?.total_loans || 0;
        const totalDeposits = portfolioData.summary?.total_deposits || 0;
        const casaRatio = depositsData.summary?.casa_ratio || 0;
        const gnpaRatio = riskData.summary?.gnpa_ratio || 0;
        const provisionCoverage = riskData.summary?.provision_coverage || 0;
        
        // CD Ratio = (Total Loans / Total Deposits) * 100
        const cdRatio = totalDeposits > 0 ? (totalLoans / totalDeposits) * 100 : 0;
        
        setKpiData({
          loanAUM: totalLoans / 10000000, // Convert to Cr
          totalDeposits: totalDeposits / 10000000, // Convert to Cr
          casaRatio: casaRatio,
          gnpaRatio: gnpaRatio,
          provisionCoverage: provisionCoverage,
          cdRatio: cdRatio,
          loanAccounts: portfolioData.summary?.loan_accounts || 0,
          depositAccounts: portfolioData.summary?.deposit_accounts || 0
        });
        
      } catch (error) {
        console.error('Error fetching KPI data:', error);
      } finally {
        setKpiLoading(false);
      }
    };
    
    fetchKPIData();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 600);
    return () => clearTimeout(timer);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 flex items-center gap-4">
          <div className="w-8 h-8 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-slate-600 font-medium">Loading financial dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative flex justify-between items-center py-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white tracking-tight">FinSight Ops</h1>
                <p className="text-xs text-slate-400">Enterprise financial reporting, analytics, and risk insights</p>
              </div>
            </div>

            <div className="relative flex items-center gap-3" ref={profileMenuRef}>
              <div className="hidden md:flex items-center gap-3 pl-3 border-l border-slate-700">
                <div className="text-right">
                  <p className="text-xs text-slate-400">Welcome back,</p>
                  <p className="text-sm font-semibold text-white">{user?.name}</p>
                </div>
              </div>

              <button
                onClick={() => setProfileMenuOpen((o) => !o)}
                className="flex items-center gap-2 p-1 rounded-full hover:bg-slate-700/50 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 focus:ring-offset-slate-900"
                aria-expanded={profileMenuOpen}
                aria-haspopup="true"
                title="Account menu"
              >
                <div className="w-9 h-9 bg-gradient-to-br from-emerald-400 to-cyan-500 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-md">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${profileMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {profileMenuOpen && (
                <div
                  className="absolute right-0 top-full mt-2 w-48 py-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50"
                  role="menu"
                >
                  {user?.role === 'admin' && (
                    <button
                      onClick={() => {
                        setProfileMenuOpen(false);
                        navigate('/admin');
                      }}
                      data-testid="admin-dashboard-btn"
                      className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-slate-700 transition-colors text-left"
                      role="menuitem"
                    >
                      <Settings className="w-4 h-4 shrink-0" />
                      <span>Admin</span>
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setProfileMenuOpen(false);
                      handleLogout();
                    }}
                    data-testid="logout-btn"
                    className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-red-600/80 transition-colors text-left"
                    role="menuitem"
                  >
                    <LogOut className="w-4 h-4 shrink-0" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        
        {/* ============ KPI STRIP (DYNAMIC) ============ */}
        <div className="bg-white rounded-xl mb-6 sticky top-[68px] z-40 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 overflow-x-auto">
            <KPIItem 
              label="Loan AUM" 
              value={kpiData?.loanAUM} 
              unit="Cr" 
              change={null}
              loading={kpiLoading}
            />
            <KPIItem 
              label="Total Deposits" 
              value={kpiData?.totalDeposits} 
              unit="Cr" 
              change={null}
              loading={kpiLoading}
            />
            <KPIItem 
              label="CASA Ratio" 
              value={kpiData?.casaRatio} 
              unit="%" 
              change={null}
              loading={kpiLoading}
            />
            <KPIItem 
              label="GNPA" 
              value={kpiData?.gnpaRatio} 
              unit="%" 
              change={null}
              inverse={true}
              loading={kpiLoading}
            />
            <KPIItem 
              label="PCR" 
              value={kpiData?.provisionCoverage} 
              unit="%" 
              change={null}
              loading={kpiLoading}
            />
            <KPIItem 
              label="CD Ratio" 
              value={kpiData?.cdRatio} 
              unit="%" 
              change={null}
              loading={kpiLoading}
            />
          </div>
        </div>

        {/* ============ ANALYTICS TABS WITH REAL DATA ============ */}
        <DashboardProvider>
          <DashboardTabs />
          <DashboardTabContent />
        </DashboardProvider>

        {/* Footer */}
        <div className="pt-6 mt-6 border-t border-slate-200">
          <p className="text-xs text-slate-400 text-center">
            Data as of {new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })} • 
            Regulatory & Compliance Reporting • Enterprise Financial Platform
          </p>
        </div>
      </main>

      {/* Floating Chat Button - FinSight AI Assistant */}
      <button
        onClick={() => navigate('/chat')}
        data-testid="finsight-ai-assistant-btn"
        className="fixed bottom-6 right-6 flex items-center gap-2.5 px-5 py-3 bg-gradient-to-r from-emerald-500 to-cyan-600 hover:from-emerald-600 hover:to-cyan-700 text-white font-semibold rounded-full shadow-xl shadow-emerald-500/30 transition-all duration-200 hover:scale-105 z-50"
      >
        <Sparkles className="w-5 h-5" />
        <span>FinSight AI Assistant</span>
      </button>
    </div>
  );
};

export default Dashboard;
